// @flow
import { of } from 'rxjs';
import { debounceTime, map, catchError, switchMap, takeUntil } from 'rxjs/operators';
import { fetchDealsApi } from './api';
import { setNewAlert } from '../alert/duck';
import { ofType, StateObservable } from 'redux-observable';
import Paginator from '../../common/Paginator';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { RootState } from '../index';

export type Deal = {
  +type: string;
  +productDealId: number;
  +url: string;
  +name: string;
  +detail: string;
  +image: string;
  +priceBefore: string;
  +priceNow: string;
  +weight: string;
  +amount: number;
  +categoryId: number;
  +originalPackaging: boolean;
  +expires: string;
  +provider: string;
  +volumeTier: {
    code: string;
    description: string;
    image: string;
    type: string;
  };
};

type DealsAction = {
  +type: string;
  +error?: HTTPError;
  +deals?: Deal[];
};

export type DealsState = {
  +isFetchingDeals?: boolean;
  +deals?: Deal[];
  +error?: HTTPError;
  +dealsPage?: number;
  +dealsPageCanAsk?: boolean;
};

// Actions
const DEALS_RESET_STATE = 'DEALS_RESET_STATE';

const DEALS_FETCH_REQUEST = 'DEALS_FETCH_REQUEST';
const DEALS_FETCH_SUCCESS = 'DEALS_FETCH_SUCCESS';
const DEALS_FETCH_ERROR = 'DEALS_FETCH_ERROR';

// Action creators
export function fetchDeals(): DealsAction {
  return {
    type: DEALS_FETCH_REQUEST,
  };
}

export function fetchDealsSuccess(deals: Deal[]): DealsAction {
  return {
    type: DEALS_FETCH_SUCCESS,
    deals,
  };
}

export function fetchDealsError(error: HTTPError): DealsAction {
  return {
    type: DEALS_FETCH_ERROR,
    error,
  };
}

export function resetDealsState(): DealsAction {
  return {
    type: DEALS_RESET_STATE,
  };
}

export const actions = {
  DEALS_RESET_STATE,
  DEALS_FETCH_REQUEST,
  DEALS_FETCH_SUCCESS,
  DEALS_FETCH_ERROR,
  fetchDeals,
  fetchDealsSuccess,
  fetchDealsError,
  resetDealsState,
};

export const initialState: DealsState = {
  dealsPage: 0,
};

const paginator = new Paginator(['deals']);

// Reducer
export default function reducer(state: DealsState = initialState, action: DealsAction): DealsState {
  switch (action.type) {
    case DEALS_FETCH_REQUEST:
      return {
        ...state,
        isFetchingDeals: true,
      };
    case DEALS_FETCH_SUCCESS:
      paginator.increment('deals', action.deals, state.deals);
      return {
        deals: (paginator.getItems('deals'): any),
        dealsPageCanAsk: paginator.getCanAsk('deals'),
        dealsPage: paginator.getPage('deals'),
      };
    case DEALS_FETCH_ERROR:
      return {
        error: action.error,
      };
    case DEALS_RESET_STATE:
      paginator.resetAll();
      return initialState;
    default:
      return state;
  }
}

export function fetchDealsEpic(action$: Observable<DealsAction>,
                               state$: StateObservable<RootState>): Observable<DealsAction> {
  return action$.pipe(
    ofType(DEALS_FETCH_REQUEST),
    debounceTime(600),
    switchMap(() => fetchDealsApi({
      page: state$.value.deals.dealsPage || paginator.getPage('deals'),
      limit: paginator.getLimit(),
    }).pipe(
      takeUntil(action$.pipe(ofType(DEALS_RESET_STATE))),
      takeUntil(action$.pipe(ofType(DEALS_FETCH_REQUEST))),
      map((response: Deal[]) => fetchDealsSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchDealsError(e),
            setNewAlert('best-deals', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchDealsError(e),
            setNewAlert('best-deals', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchDealsError(e));
      }))
    ));
}




// WEBPACK FOOTER //
// ./src/modules/best-deals/duck.js